import { Audience, ClinicalArea, Topic } from '../types/metadata';
import type { CatalogFilterFacet } from './resourceLinks';

/** Profile and home still store the website enums. The library query uses Tools names. */

const AUDIENCE_TO_CATALOG: Partial<Record<Audience, string>> = {
  [Audience.Nurses]: 'Nurses',
  [Audience.Physicians]: 'Clinicians',
  [Audience.Transfusionists]: 'TransfusionMedicineProfessionals',
  [Audience.Laboratory]: 'LaboratoryProfessionals',
  [Audience.Administrators]: 'Administrators',
  [Audience.QualitySafety]: 'QualitySafetyProfessionals',
  [Audience.Educators]: 'Educators',
  [Audience.StudentsTrainees]: 'StudentsTrainees',
  [Audience.DataAnalytics]: 'DataAnalysts',
  [Audience.PolicyRegulators]: 'PolicyMakersRegulators',
  [Audience.PatientsCaregivers]: 'PatientsCaregivers',
};

const CLINICAL_AREA_TO_CATALOG: Partial<Record<ClinicalArea, string[]>> = {
  [ClinicalArea.Obstetrics]: ['ObstetricsMidwifery'],
  [ClinicalArea.PediatricsNeonatology]: ['Pediatrics', 'Neonatology'],
  [ClinicalArea.SurgeryPerioperative]: ['SurgeryOperativeCare'],
};

const SAME_CLINICAL_AREAS = new Set<ClinicalArea>([
  ClinicalArea.TransfusionMedicine,
  ClinicalArea.Hematology,
  ClinicalArea.Neurology,
  ClinicalArea.Rheumatology,
  ClinicalArea.Anesthesiology,
  ClinicalArea.Emergency,
  ClinicalArea.ICUCriticalCare,
  ClinicalArea.Oncology,
  ClinicalArea.Cardiology,
  ClinicalArea.InternalMedicine,
  ClinicalArea.HomeInfusion,
  ClinicalArea.PrimaryCare,
  ClinicalArea.Dermatology,
  ClinicalArea.Immunology,
  ClinicalArea.InfectiousDiseases,
  ClinicalArea.Transplant,
  ClinicalArea.Other,
]);

type InterestFacet = Exclude<CatalogFilterFacet, 'audience'>;

const TOPIC_TO_CATALOG: Partial<Record<Topic, { facet: InterestFacet; values: string[] }>> = {
  [Topic.IVIG]: { facet: 'topic', values: ['Immunoglobulins'] },
  [Topic.SCIG]: { facet: 'topic', values: ['Immunoglobulins'] },
  [Topic.ImmuneSerumGlobulin]: { facet: 'topic', values: ['Immunoglobulins'] },
  [Topic.IGOrderingOntario]: { facet: 'topic', values: ['IGOrderingOntario'] },
  [Topic.Utilization]: { facet: 'topic', values: ['Utilization'] },
  [Topic.MHP]: { facet: 'topic', values: ['MassiveHemorrhageProtocol'] },
  [Topic.Shortage]: { facet: 'topic', values: ['ContingencyShortages'] },
  [Topic.Redistribution]: { facet: 'topic', values: ['Redistribution'] },
  [Topic.InventoryManagement]: { facet: 'topic', values: ['InventoryManagement'] },
  [Topic.RBC]: { facet: 'topic', values: ['RedBloodCellsRBC'] },
  [Topic.Plasma]: { facet: 'topic', values: ['FreshFrozenPlasma'] },
  [Topic.ABPlasma]: { facet: 'topic', values: ['ABPlasma'] },
  [Topic.Platelets]: { facet: 'topic', values: ['Platelets'] },
  [Topic.Cryoprecipitate]: { facet: 'topic', values: ['Cryoprecipitate'] },
  [Topic.Albumin]: { facet: 'topic', values: ['Albumin'] },
  [Topic.C1EsteraseInhibitors]: { facet: 'topic', values: ['C1EsteraseInhibitors'] },
  [Topic.FetalGenotyping]: { facet: 'topic', values: ['FetalGenotyping'] },
  [Topic.Neonatal]: { facet: 'topic', values: ['NeonatalTransfusionCare'] },
  [Topic.Prenatal]: { facet: 'clinicalArea', values: ['ObstetricsMidwifery'] },
  [Topic.Postnatal]: { facet: 'clinicalArea', values: ['ObstetricsMidwifery'] },
  [Topic.Rounds]: { facet: 'topic', values: ['Rounds'] },
  [Topic.BootCamp]: { facet: 'topic', values: ['BootCamp'] },
  [Topic.Symposium]: { facet: 'topic', values: ['Symposium'] },
  [Topic.GHEST]: { facet: 'topic', values: ['GHEST'] },
  [Topic.BloodSafety]: { facet: 'topic', values: ['BloodSafety'] },
  [Topic.PublicHealth]: { facet: 'topic', values: ['PublicHealth'] },
  [Topic.Research]: { facet: 'topic', values: ['Research'] },
  [Topic.Quality]: { facet: 'topic', values: ['QualityImprovement'] },
  [Topic.Safety]: { facet: 'topic', values: ['PatientSafety'] },
  [Topic.ORBCoNAnnualReport]: { facet: 'topic', values: ['ORBCoNAnnualReport'] },
  [Topic.ORBCoNReport]: { facet: 'topic', values: ['ORBCoNReport'] },
  [Topic.ValidationVerification]: { facet: 'topic', values: ['ValidationVerification'] },
  [Topic.ProspectiveScreening]: { facet: 'topic', values: ['ProspectiveScreening'] },
  [Topic.TransfusionistsTalk]: { facet: 'topic', values: ['TransfusionistsTalk'] },
  [Topic.eLearning]: { facet: 'contentType', values: ['Educational_Training'] },
  [Topic.TrainingEducation]: { facet: 'contentType', values: ['Educational_Training'] },
  [Topic.Education]: { facet: 'contentType', values: ['Educational_Training'] },
  [Topic.Guidelines]: { facet: 'contentType', values: ['Clinical_Guidelines_Recommendations'] },
  [Topic.Procedures]: { facet: 'contentType', values: ['Procedures'] },
  [Topic.Templates]: { facet: 'contentType', values: ['Template_Form'] },
  [Topic.Forms]: { facet: 'contentType', values: ['Template_Form'] },
  [Topic.Checklists]: { facet: 'contentType', values: ['Template_Form'] },
  [Topic.Audit]: { facet: 'contentType', values: ['Audit_Report'] },
  [Topic.ProvincialAuditReport]: { facet: 'contentType', values: ['Audit_Report'] },
  [Topic.Webinar]: { facet: 'contentType', values: ['Event_Webinar'] },
  [Topic.DataDashboard]: { facet: 'contentType', values: ['Dashboard'] },
  [Topic.PatientFactSheets]: { facet: 'contentType', values: ['Patient_Fact_Sheets'] },
  [Topic.ExternalLink]: { facet: 'contentType', values: ['External_Resource'] },
};

export function catalogAudience(audience: Audience): string | null {
  return AUDIENCE_TO_CATALOG[audience] ?? null;
}

export function catalogClinicalAreas(areas: readonly ClinicalArea[]): string[] {
  const values = new Set<string>();
  for (const area of areas) {
    const mapped = CLINICAL_AREA_TO_CATALOG[area];
    if (mapped) mapped.forEach((value) => values.add(value));
    else if (SAME_CLINICAL_AREAS.has(area)) values.add(area);
  }
  return [...values];
}

export interface CatalogInterestQuery {
  topic: string[];
  clinicalArea: string[];
  contentType: string[];
}

export function catalogInterestQuery(
  topics: readonly Topic[],
  areas: readonly ClinicalArea[]
): CatalogInterestQuery {
  const query: CatalogInterestQuery = { topic: [], clinicalArea: [], contentType: [] };
  const add = (facet: InterestFacet, values: string[]) => {
    const bucket = new Set(query[facet]);
    values.forEach((value) => bucket.add(value));
    query[facet] = [...bucket];
  };
  for (const topic of topics) {
    const mapped = TOPIC_TO_CATALOG[topic];
    if (mapped) add(mapped.facet, mapped.values);
  }
  add('clinicalArea', catalogClinicalAreas(areas));
  return query;
}

export function interestMatchesCatalog(
  topic: Topic,
  item: { topics: string[]; clinicalAreas: string[]; contentType: string | null }
): boolean {
  const mapped = TOPIC_TO_CATALOG[topic];
  if (!mapped) return false;
  if (mapped.facet === 'topic') return mapped.values.some((value) => item.topics.includes(value));
  if (mapped.facet === 'contentType') return item.contentType != null && mapped.values.includes(item.contentType);
  return mapped.values.some((value) => item.clinicalAreas.includes(value));
}
